/**
 * CareerOpportunities Page
 * Main career section with job listings, filters, and search
 * MOBILE RESPONSIVE VERSION
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Briefcase, 
  Building, 
  Clock,
  Users,
  FileText,
  Mic,
  ChevronDown,
  X,
  Menu
} from 'lucide-react';
import JobCard from '../components/JobCard';
import ApplicationModal from '../components/ApplicationModal';
import useJobs from '../hooks/useJobs';
import { useAuth } from '../../../core/contexts/AuthContext';
import { hasUserApplied } from '../services/careerService';

/**
 * CareerOpportunities Page Component
 */
const CareerOpportunities = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    jobType: '',
    location: '',
    experienceLevel: '',
    company: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [appliedJobs, setAppliedJobs] = useState(new Set());
  const [showMobileActions, setShowMobileActions] = useState(false);

  // Use custom hook for jobs data
  const { 
    jobs, 
    loading, 
    error, 
    hasMore, 
    loadMore, 
    categories,
    companies 
  } = useJobs(filters, searchTerm);

  // Check which jobs user has applied to
  useEffect(() => {
    const checkApplications = async () => {
      if (!currentUser || !jobs.length) return;

      const applicationChecks = await Promise.all(
        jobs.map(job => hasUserApplied(currentUser.uid, job.id))
      );

      const appliedJobIds = new Set();
      applicationChecks.forEach((hasApplied, index) => {
        if (hasApplied) {
          appliedJobIds.add(jobs[index].id);
        }
      });

      setAppliedJobs(appliedJobIds);
    };

    checkApplications();
  }, [currentUser, jobs]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    // Search is handled by the useJobs hook
  };

  // Handle filter change
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      jobType: '',
      location: '',
      experienceLevel: '',
      company: ''
    });
    setSearchTerm('');
  };

  // Handle job application
  const handleApply = (job) => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }
    
    setSelectedJob(job);
    setShowApplicationModal(true);
  };

  // Handle successful application
  const handleApplicationSuccess = (jobId) => {
    setAppliedJobs(prev => new Set([...prev, jobId]));
    setShowApplicationModal(false);
    setSelectedJob(null);
  };

  // Filter options
  const filterOptions = {
    jobType: ['full-time', 'part-time', 'contract', 'internship', 'remote'],
    experienceLevel: ['entry-level', 'mid-level', 'senior-level', 'executive'],
    location: ['Cape Town', 'Johannesburg', 'Durban', 'Pretoria', 'Remote', 'Hybrid']
  };

  // Get active filter count
  const activeFilterCount = Object.values(filters).filter(filter => filter).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 py-4 sm:py-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          {/* Title and Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Career Opportunities
              </h1>
              <p className="text-gray-600 text-sm sm:text-base">
                Find your next career opportunity from top companies
              </p>
            </div>
            
            {/* Quick Actions - Desktop */}
            <div className="hidden sm:flex items-center space-x-3">
              <Link
                to="/career/cv-manager"
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <FileText className="w-4 h-4 mr-2" />
                Manage CV
              </Link>
              <Link
                to="/career/screening"
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Mic className="w-4 h-4 mr-2" />
                Audio Screening
              </Link>
            </div>

            {/* Mobile Actions Button */}
            <div className="sm:hidden">
              <button
                onClick={() => setShowMobileActions(!showMobileActions)}
                className="flex items-center justify-center w-10 h-10 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Mobile Actions Dropdown */}
          {showMobileActions && (
            <div className="sm:hidden mb-4 bg-white rounded-lg shadow-lg border p-2">
              <Link
                to="/career/cv-manager"
                className="flex items-center w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-100 rounded-md"
                onClick={() => setShowMobileActions(false)}
              >
                <FileText className="w-4 h-4 mr-2" />
                Manage CV
              </Link>
              <Link
                to="/career/screening"
                className="flex items-center w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-100 rounded-md"
                onClick={() => setShowMobileActions(false)}
              >
                <Mic className="w-4 h-4 mr-2" />
                Audio Screening
              </Link>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{jobs.length}</p>
                  <p className="text-xs sm:text-sm text-gray-600">Active Jobs</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <Building className="w-6 h-6 sm:w-8 sm:h-8 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{companies.length}</p>
                  <p className="text-xs sm:text-sm text-gray-600">Companies</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <Users className="w-6 h-6 sm:w-8 sm:h-8 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{appliedJobs.size}</p>
                  <p className="text-xs sm:text-sm text-gray-600">Applied Jobs</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                    {jobs.filter(job => job.isUrgent).length}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">Urgent Hiring</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mb-6">
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder="Search jobs, companies, or skills..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>
          </form>

          {/* Filter Toggle */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 transition-colors text-sm sm:text-base"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-2 bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                  {activeFilterCount}
                </span>
              )}
              <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${
                showFilters ? 'rotate-180' : ''
              }`} />
            </button>

            {/* Clear Filters */}
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-sm text-green-600 hover:text-green-700 flex items-center"
              >
                <X className="w-3 h-3 mr-1" />
                Clear all
              </button>
            )}
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="mt-4 space-y-4 pt-4 border-t border-gray-200">
              {/* Mobile Filters - Stack vertically */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Job Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Type
                  </label>
                  <select
                    value={filters.jobType}
                    onChange={(e) => handleFilterChange('jobType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  >
                    <option value="">All Types</option>
                    {filterOptions.jobType.map(type => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Location Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <select
                    value={filters.location}
                    onChange={(e) => handleFilterChange('location', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  >
                    <option value="">All Locations</option>
                    {filterOptions.location.map(location => (
                      <option key={location} value={location}>
                        {location}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Experience Level Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experience Level
                  </label>
                  <select
                    value={filters.experienceLevel}
                    onChange={(e) => handleFilterChange('experienceLevel', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  >
                    <option value="">All Levels</option>
                    {filterOptions.experienceLevel.map(level => (
                      <option key={level} value={level}>
                        {level.charAt(0).toUpperCase() + level.slice(1).replace('-', ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Company Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company
                  </label>
                  <select
                    value={filters.company}
                    onChange={(e) => handleFilterChange('company', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  >
                    <option value="">All Companies</option>
                    {companies.map(company => (
                      <option key={company.name} value={company.name}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Job Categories */}
        {categories.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Browse by Category</h2>
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category.category}
                  onClick={() => handleFilterChange('jobType', category.category)}
                  className={`px-3 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${
                    filters.jobType === category.category
                      ? 'bg-green-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {category.category.charAt(0).toUpperCase() + category.category.slice(1).replace('-', ' ')} 
                  <span className="ml-1 text-xs">({category.count})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Job Listings */}
        <div className="space-y-4">
          {loading && jobs.length === 0 ? (
            <div className="flex justify-center py-8 sm:py-12">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading job opportunities...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-8 sm:py-12">
              <p className="text-red-600 mb-4">Error loading jobs: {error}</p>
              <button 
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Try Again
              </button>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <Briefcase className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
              <p className="text-gray-600 mb-4 px-4">
                Try adjusting your search criteria or filters
              </p>
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              {jobs.map(job => (
                <JobCard
                  key={job.id}
                  job={job}
                  onApply={handleApply}
                  hasApplied={appliedJobs.has(job.id)}
                />
              ))}

              {/* Load More Button */}
              {hasMore && (
                <div className="text-center py-6">
                  <button
                    onClick={loadMore}
                    disabled={loading}
                    className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                      loading
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {loading ? 'Loading...' : 'Load More Jobs'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Application Modal */}
      {showApplicationModal && selectedJob && (
        <ApplicationModal
          job={selectedJob}
          onClose={() => {
            setShowApplicationModal(false);
            setSelectedJob(null);
          }}
          onSuccess={() => handleApplicationSuccess(selectedJob.id)}
        />
      )}
    </div>
  );
};

export default CareerOpportunities;